import PropTypes from 'prop-types';
import { extendObservable, action, reaction } from 'mobx';
import { apiCall } from '../../../API';
import AsyncActionStore from '../../AsyncActionStore';
import { REV_TYPE } from '../../AuditStore';
import ConcurrencyPopUpStore from '../../ConcurrencyPopUpStore';
import FlagStore from '../../FlagStore';
import RadioButtonStore from '../../RadioButtonStore';
import PolicyTableStore from '../PolicyTableStore';
import UserGroupsStore from './UserGroupsStore';
import BrowserIsolationOptionsStore from './BrowserIsolationOptionsStore';

export const USER_GROUPS_SOURCES = {
    NONE: 'none',
    SAML: 'META',
    TAP: 'USER_CENTER',
};

export default class BrowsingRolesStore {
    static PATH = '/console/browsing-roles';
    static PUBLIC_PATH = '/api/v3/browsing-roles';

    static PropType = PropTypes.shape({
        policyTable: PolicyTableStore.PropType.isRequired,

        userGroupsSource: RadioButtonStore.PropType,
        userGroupsSourceForm: RadioButtonStore.PropType,

        aboutExpanded: FlagStore.PropType.isRequired,
        userGroupSourceModal: FlagStore.PropType,

        load: PropTypes.func.isRequired,
        loadStatus: AsyncActionStore.PropType,

        update: PropTypes.func.isRequired,
        updateStatus: AsyncActionStore.PropType,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class BrowsingRolesStore */{
            policyTable: new PolicyTableStore({
                apiList: apiCall(`${BrowsingRolesStore.PATH}/list`),
                apiChangePriorities: apiCall(`${BrowsingRolesStore.PATH}/rearrange`),
                modalSections: {
                    userGroups: new UserGroupsStore(),
                    browserIsolationOptions: new BrowserIsolationOptionsStore(),
                },
                requireEditConfirmation: () =>
                    this.policyTable.policyModal.sections.userGroups.effectsEmails.hasChanged(),
                concurrencyPopUpStore: new ConcurrencyPopUpStore({
                    apiVersionedUpdate: apiCall(`${BrowsingRolesStore.PATH}/versioned/update`),
                    apiUpdate: apiCall(`${BrowsingRolesStore.PATH}/update`),
                    apiCreate: apiCall(`${BrowsingRolesStore.PATH}/create`),
                    apiVersionedDelete: apiCall(`${BrowsingRolesStore.PATH}/versioned/delete`),
                    apiDelete: ({ id }) => apiCall(`${BrowsingRolesStore.PATH}/delete/${id}`)(),
                    type: REV_TYPE.BROWSING_ROLE,
                }),
            }),

            userGroupsSource: new RadioButtonStore(USER_GROUPS_SOURCES.NONE),
            userGroupsSourceForm: new RadioButtonStore(USER_GROUPS_SOURCES.NONE),

            aboutExpanded: new FlagStore(true),
            userGroupSourceModal: new FlagStore(false),
            allowedUserGroupsSources: {
                [USER_GROUPS_SOURCES.NONE]: true,
            },

            loadStatus: new AsyncActionStore(),
            apiCustomerSettingsLoad: apiCall('/console/customer/settings/get'),

            updateStatus: new AsyncActionStore(),
            apiUserGroupSourceUpdate: apiCall('/console/customer/settings/update'),
        }, data);

        reaction(
            this.userGroupsSource.getValue,
            (v) => {
                this.policyTable.policyModal.sections.userGroups.source = v;
            }
        );
    }

    setAllowedUserGroupsSources = action((map) => {
        this.allowedUserGroupsSources = map;
    });

    openEditModal = action(() => {
        this.userGroupsSourceForm.setValue(this.userGroupsSource.getValue());
        this.userGroupsSourceForm.commit();
        this.userGroupSourceModal.open();
    });

    load = action(() => {
        if (!this.loadStatus.isLoading) {
            this.loadStatus.setLoading(this.apiCustomerSettingsLoad()
                .then((config) => {
                    let { userGroupSource } = config;
                    userGroupSource = userGroupSource || USER_GROUPS_SOURCES.NONE;
                    this.userGroupsSource.setValue(userGroupSource);
                    this.userGroupsSource.commit();
                    return config;
                }));
        }
        return this.loadStatus.promise;
    });

    update = action((customerId) => {
        if (!this.updateStatus.isLoading) {
            const userGroupSource = this.userGroupsSourceForm.getValue();

            const data = {
                userGroupSource: userGroupSource === USER_GROUPS_SOURCES.NONE ? null : userGroupSource,
                id: customerId,
            };
            this.updateStatus.setLoading(this.apiUserGroupSourceUpdate(data)
                .then(() => this.load())
                .then(() => this.policyTable.policyModal.sections.userGroups.load())
                .then(() => this.userGroupSourceModal.close()),
            );
        }
        return this.updateStatus.promise;
    });
}
